import { useEffect, useState } from "react";
import CustomTableWithPagination from "../../../../reusable-components/CustomTable";
import CustomerDatabaseService from "../../../../services/customerDatabase.service";
import { DatabaseCommonTableProps } from "../../../../Types/DatabaseTabTypes";
import { getColumns, jobTableConstants } from "./constants";
import styles from "./JobTable.module.scss";
import TNButton from "../../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
  CommonButtonTexts,
} from "../../../../enums/ButtonAttributesEnums";
import { useHistory, useLocation } from "react-router-dom";
import { ViewListingTypes } from "../../../../enums/ViewListingTypes";
import { useWrapperContext } from "../../../../context/WrapperContext";
import { useMaterials } from "../../../../context/materialListingContext";
import { useTruckDetailContext } from "../../../../context/TruckDetailsContext";
import CreateJobServices from "../../../../services/createJob.services";
import TNLoader from "../../../../reusable-components/TNLoader";
import { appRoutes } from "../../../../constants/appRoutes";
import RolesServices from "../../../../services/roles.services";
import { UserRoleActions } from "../../../../enums/UserRolesEnum";
import { Input, message, Modal, Switch } from "antd";
import JobDatabaseService from "../../../../services/jobDatabase.service";
import InputField from "../../../../reusable-components/TNInputField";
import SearchIcon from "../../../../assets/images/search-button.svg";

const JobTable = (props?: DatabaseCommonTableProps) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // search debounce template
  useEffect(() => {
    const debounce = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 1500);

    return () => clearTimeout(debounce);
  }, [searchInput]);

  const { section, sectionId } = props;
  const navigate = useHistory();
  const location = useLocation();
  const { setPrevRecord } = useWrapperContext();
  const { resetTruckMaterial } = useMaterials();
  const { resetTruckDetails } = useTruckDetailContext();
  const { GetJobDetails } = CreateJobServices();
  const { BlockUnblockJobs } = JobDatabaseService();
  const {
    mutateAsync: fetchJobDetails,
    isPending: isFetchingJobDetailsPending,
  } = GetJobDetails();
  const { mutateAsync: blockUnblockJob, isPending: blockUnblockJobPending } =
    BlockUnblockJobs();
  const {
    BUTTON: { ADD_JOB_LABEL },
    NO_ACCESS_JOB_DB,
    SHOW_BLOCKED_TOGGLE,
    BLOCK_TITLE,
    UNBLOCK_TITLE,
    BLOCK_MESSAGE,
    UNBLOCK_MESSAGE,
  } = jobTableConstants;
  const { FetchJobsTable } = CustomerDatabaseService();
  const [showBlocked, setShowBlocked] = useState<boolean>(
    !!location.state?.isBlocked,
  );
  const [blockUnblockConfirmVisible, setBlockUnblockConfirmVisible] =
    useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(
    undefined,
  );
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const handleCurrentPage = (page: number) => {
    setCurrentPage(page);
  };
  const handlePagination = (page: number) => handleCurrentPage(page);
  const handlePaginationPageSize = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const userDetails = localStorage.getItem("userDetails");
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.["userRoleInfo"];
  const userRoleProfileInfo = userDetailsJson?.["userProfile"];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);

  const noActionDropFields = roleDetails
    ? roleDetails?.[0]?.otherData?.[1]?.multiPannel?.[0]?.selectedOptions?.includes(
        UserRoleActions.EDIT,
      )
    : true;

  const jobDatabaseAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[2]?.multiPannel?.[1]?.selectedOptions?.includes(
        UserRoleActions.ALLOW,
      )
    : true;

  const handleAddJob = () => {
    if (!noActionDropFields) {
      return;
    }
    navigate.push(`/app/add-trucking-material${window.location?.hash}`, {
      from: location,
      source: appRoutes?.CUSTOMER_DATABASE,
      selectedCustomer: sectionId,
    });
  };

  const handleBlockUnblockConfirmOpen = () =>
    setBlockUnblockConfirmVisible(true);
  const handleBlockUnblockConfirmClose = () => {
    setBlockUnblockConfirmVisible(false);
    setSelectedJobId(undefined);
  };

  const handleShowBlocked = (checked: boolean) => {
    setCurrentPage(1);
    setShowBlocked(checked);
    navigate.replace(location.pathname, { ...location.state, isBlocked: checked });
  };

  const handleBlockUnblockClick = (id: string) => {
    setSelectedJobId(id);
    handleBlockUnblockConfirmOpen();
  };

  const handleBlockUnblockAction = async () => {
    await blockUnblockJob(
      {
        jobId: selectedJobId,
        blockAction: !!!showBlocked,
      },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
    handleBlockUnblockConfirmClose();
  };

  const handleEditJob = async (id?: string) => {
    const data = await fetchJobDetails(id);
    setPrevRecord(data);
    resetTruckDetails();
    resetTruckMaterial();
    navigate.push(
      `/app/update-trucking-material/${id}#${ViewListingTypes.LIST}`,
      {
        edit: true,
        step: 2,
        navigatedFromCxDb: true,
        from: location,
        source: appRoutes.CUSTOMER_DATABASE,
      },
    );
  };

  const handleViewNavigate = (row?: object) => {
    if (jobDatabaseAccess)
      navigate.push(`${appRoutes.JOB_PROFILE}/${row?.id}`, {
        jobName: row?.jobName,
        isBlocked: showBlocked,
      });
    else message.error(NO_ACCESS_JOB_DB);
  };

  const { data, refetch, isFetching } = FetchJobsTable({
    section: section,
    section_id: sectionId,
    page: currentPage,
    perPage: pageSize,
    isBlocked: showBlocked,
    search: searchTerm,
  });
  const columns = getColumns({
    handleEditNavigate: handleEditJob,
    noActionDropFields: noActionDropFields,
    handleBlockUnblock: handleBlockUnblockClick,
    blockedJob: showBlocked,
  });

  const handleSearchInput = (value) => {
    setSearchInput(value);
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  return (
    <>
      <TNLoader
        isLoading={isFetchingJobDetailsPending || blockUnblockJobPending}
      />
      <Modal
        visible={blockUnblockConfirmVisible}
        okText={CommonButtonTexts.CONFIRM}
        title={showBlocked ? UNBLOCK_TITLE : BLOCK_TITLE}
        onOk={handleBlockUnblockAction}
        onCancel={handleBlockUnblockConfirmClose}
      >
        {showBlocked ? UNBLOCK_MESSAGE : BLOCK_MESSAGE}
      </Modal>

      <Input
        placeholder={jobTableConstants.SEARCH}
        className={styles.searchField}
        onChange={(e) => handleSearchInput(e.target.value)}
        addonAfter={
          <TNButton
            shape={ButtonShape.DEFAULT}
            size={ButtonSize.SMALL}
            block
            text={<img className={styles.searchFieldIcon} src={SearchIcon} />}
            onClick={() => {
              handleSearch();
            }}
          />
        }
        onKeyDown={(e) => {
          if (e.key === jobTableConstants.ENTER_KEY) {
            e.preventDefault();
            handleSearch();
          }
        }}
      />

      <CustomTableWithPagination
        columns={columns}
        className={styles.Table}
        data={data?.data}
        pageSize={pageSize}
        currentPage={currentPage}
        rowKey={(record) => record?.id}
        handleSizeChangerChange={handlePaginationPageSize}
        handleChange={handlePagination}
        loading={isFetching}
        totalData={data?.meta?.totalRecords}
        isClickableTable
        handleSelectedRow={handleViewNavigate}
      />
      <div className={styles.actionItems}>
        <div className={styles.blockedToggle}>
          {SHOW_BLOCKED_TOGGLE}
          <Switch checked={showBlocked} onClick={handleShowBlocked} />
        </div>
        <TNButton
          text={ADD_JOB_LABEL}
          type={ButtonType.PRIMARY}
          size={ButtonSize.LARGE}
          shape={ButtonShape.DEFAULT}
          onClick={handleAddJob}
          disabled={!noActionDropFields}
        />
      </div>
    </>
  );
};

export default JobTable;
